<template>
  <LayoutPage title="RVDefender Inventory">
    <template #actions>
      <div class="mr-4" style="min-width: 240px">
        <SearchField v-model="search" />
      </div>
    </template>
    <v-row>
      <v-col lg="6">
        <CardLayout title="Assigned Devices" :loading="facilityDevicesLoading">
          <v-simple-table>
            <thead>
              <tr>
                <th>Renter</th>
                <th>Primary Phone</th>
                <th>Device #</th>
                <th>Device Health</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody v-if="facilityDevicesLoading">
              <tr>
                <td colspan="5">
                  <em>Loading assigned devices...</em>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="assignedDevices.length > 0">
              <tr v-for="device in assignedDevices" :key="device.id">
                <td style="white-space: nowrap">
                  {{ device.renter.firstName }} {{ device.renter.lastName }}
                </td>
                <td style="white-space: nowrap">
                  {{ formatPhoneNumber(device.renter.primaryPhone) }}
                </td>

                <td style="white-space: nowrap">
                  {{ device.short_id || device.alias }}
                </td>
                <td style="white-space: nowrap">
                  <DeviceGaugeCluster :device="device" />
                </td>

                <td>
                  <v-btn
                    depressed
                    small
                    tile
                    color="warning"
                    @click="showWarning(device)"
                  >
                    Remove Device
                  </v-btn>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="5">
                  <em>No devices are assigned</em>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <DialogLayout
            v-if="deviceToRemove"
            v-model="openRemoveDeviceWarning"
            title="Remove Device"
          >
            <p>
              Has renter {{ deviceToRemove.renter.firstName }}
              {{ deviceToRemove.renter.lastName }} physically returned device
              {{ deviceToRemove.short_id || deviceToRemove.alias }}?
            </p>
            <template #actions>
              <v-btn
                color="primary"
                text
                @click="openRemoveDeviceWarning = false"
              >
                No
              </v-btn>
              <v-btn color="primary" text @click="removeDevice()"> Yes </v-btn>
            </template>
          </DialogLayout>
        </CardLayout>
      </v-col>

      <v-col lg="6">
        <CardLayout
          title="Available RVDefender Inventory"
          :loading="facilityDevicesLoading"
        >
          <v-simple-table>
            <thead>
              <tr>
                <th>Device #</th>
                <th>Device Health</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody v-if="facilityDevicesLoading">
              <tr>
                <td colspan="3">
                  <em>Loading devices from inventory...</em>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="availableDevices.length > 0">
              <tr v-for="device in availableDevices" :key="device.id">
                <td style="width: 40%; white-space: nowrap">
                  {{ device.short_id || device.alias }}
                </td>
                <td style="width: 40%; white-space: nowrap">
                  <DeviceGaugeCluster :device="device" />
                </td>
                <td style="white-space: nowrap">
                  <v-btn
                    :disabled="device.status !== 'ok'"
                    depressed
                    small
                    tile
                    color="success"
                    @click="showDeviceAssignDialog(device)"
                  >
                    Assign Device
                  </v-btn>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="3">
                  <em>No RVDefenders are available</em>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <DialogLayout
            v-if="deviceToAssign"
            v-model="openDeviceAssignDialog"
            :title="`Assigning Device #${
              deviceToAssign.short_id || deviceToAssign.alias
            }`"
            width="750px"
          >
            <v-row>
              <v-col cols="3">
                <v-radio-group
                  v-model="addRenterDeviceOption"
                  style="margin-bottom: 76px"
                >
                  <v-radio label="Select Previous Renter" value="existing" />
                  <v-radio label="Add New Renter" value="new" />
                </v-radio-group>
              </v-col>

              <v-col cols="9">
                <div
                  v-if="addRenterDeviceOption === 'existing'"
                  style="margin-top: 16px"
                >
                  <v-select
                    v-model="selectedRenter"
                    :items="existingRenters"
                    item-text="info"
                    item-value="id"
                    label="Choose Renter"
                    return-object
                    dense
                    outlined
                  />
                </div>

                <div
                  v-if="addRenterDeviceOption === 'new'"
                  style="margin-top: 56px"
                >
                  <v-row>
                    <v-col cols="6">
                      <ValidatedTextField
                        v-model="newRenter.firstName"
                        name="First Name"
                        rules="required|max:250"
                      />
                    </v-col>
                    <v-col cols="6">
                      <ValidatedTextField
                        v-model="newRenter.lastName"
                        name="Last Name"
                        rules="required|max:250"
                      />
                    </v-col>
                  </v-row>
                  <InputPhoneNumber v-model="newRenter.primaryPhone" required />
                </div>
              </v-col>
            </v-row>

            <template #actions>
              <v-btn
                color="primary"
                text
                @click="openDeviceAssignDialog = false"
              >
                Cancel
              </v-btn>
              <v-btn color="primary" text @click="addDevice()"> Assign </v-btn>
            </template>
          </DialogLayout>
        </CardLayout>
      </v-col>
    </v-row>
  </LayoutPage>
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import CardLayout from "@layout/CardLayout.vue";
import DialogLayout from "@layout/DialogLayout.vue";
import SearchField from "@atoms/SearchField.vue";
import DeviceGaugeCluster from "@components/common/atoms/DeviceGaugeCluster.vue";
import InputPhoneNumber from "@tod-ui/components/InputPhoneNumber.vue";
import ValidatedTextField from "@components/utils/ValidatedTextField.vue";

import useAlerts from "@tod-ui/composables/useAlerts";
import { useXpmInventory } from "./useXpmInventory";
import { formatPhoneNumber } from "@tod-ui/helpers/phone";
import { computed, ref } from "vue";

export default {
  name: "PageRVDefenderInventory",
  components: {
    LayoutPage,
    CardLayout,
    DialogLayout,
    SearchField,
    DeviceGaugeCluster,
    ValidatedTextField,
    InputPhoneNumber,
  },
  setup() {
    const {
      devicesWithRenters,
      devicesWithoutRenters,
      setApolloClient,
      fetchFacilityRenters,
      addRenterDevice,
      createRenterAndAddDevice,
      removeRenterDevice,
      facilityDevicesLoading,
    } = useXpmInventory();

    const { addAlert } = useAlerts();

    const search = ref("");
    const assignedDevices = computed(() =>
      devicesWithRenters.value.filter((device) => {
        const renterText = `${device.renter.firstName} ${
          device.renter.lastName
        } ${formatPhoneNumber(device.renter.primaryPhone)}`.toLowerCase();
        const deviceText = (
          device.short_id ||
          device.alias ||
          ""
        ).toLowerCase();
        const searchTerm = search.value.toLowerCase();
        return (
          renterText.includes(searchTerm) || deviceText.includes(searchTerm)
        );
      })
    );
    const availableDevices = computed(() =>
      devicesWithoutRenters.value.filter((device) => {
        const deviceText = (
          device.short_id ||
          device.alias ||
          ""
        ).toLowerCase();
        const searchTerm = search.value.toLowerCase();
        return deviceText.includes(searchTerm);
      })
    );
    const openDeviceAssignDialog = ref(false);
    const deviceToAssign = ref(null);
    const addRenterDeviceOption = ref("existing");
    const existingRenters = ref([]);
    const selectedRenter = ref(null);
    const newRenter = ref({ firstName: "", lastName: "", primaryPhone: "" });
    const openRemoveDeviceWarning = ref(false);
    const deviceToRemove = ref(null);

    async function showDeviceAssignDialog(device) {
      deviceToAssign.value = device;
      openDeviceAssignDialog.value = true;
      const facilityRenters = await fetchFacilityRenters();
      existingRenters.value = facilityRenters.map((renter) => ({
        ...renter,
        info: `${renter.firstName} ${renter.lastName}: ${formatPhoneNumber(
          renter.primaryPhone
        )}`,
      }));
    }

    function showWarning(device) {
      deviceToRemove.value = device;
      openRemoveDeviceWarning.value = true;
    }

    async function addDevice() {
      openDeviceAssignDialog.value = false;
      let result = {};
      if (addRenterDeviceOption.value === "existing" && selectedRenter.value) {
        result = await addRenterDevice(
          deviceToAssign.value,
          selectedRenter.value
        );
      } else if (addRenterDeviceOption.value === "new") {
        result = await createRenterAndAddDevice(
          deviceToAssign.value,
          newRenter.value
        );
        newRenter.value = { firstName: "", lastName: "", primaryPhone: "" };
      }
      if (result.error) {
        addAlert({ message: result.error, timeout: 20 });
      }
    }

    async function removeDevice() {
      openRemoveDeviceWarning.value = false;
      const result = await removeRenterDevice(deviceToRemove);
      if (result.error) {
        addAlert({ message: result.error, timeout: 20 });
      }
    }

    return {
      search,
      assignedDevices,
      availableDevices,
      facilityDevicesLoading,
      deviceToAssign,
      deviceToRemove,
      addRenterDeviceOption,
      existingRenters,
      selectedRenter,
      newRenter,
      openDeviceAssignDialog,
      openRemoveDeviceWarning,
      formatPhoneNumber,
      showDeviceAssignDialog,
      showWarning,
      addDevice,
      removeDevice,
      setApolloClient,
    };
  },
  mounted() {
    this.setApolloClient(this.$apolloProvider);
  },
};
</script>

<style scoped></style>
